import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { App } from "@capacitor/app";
import { Device } from "@capacitor/device";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import * as Sentry from "@sentry/capacitor";
import { distinctUntilChanged, map } from "rxjs";
import { MemoryStats, MemoryStatsResult } from "../../plugins/memory-stats";
import { MemoryTrail, TRAIL_STEP_S } from "./memory-trail";
import { fppProvider } from "../../plugins/opsctrl-foc/class/providers/fppProvider";
import { EnvironmentService } from "../env/env.service";
import { SettingsService } from "../settings/settings.service";

// Scope context refresh runs faster than metric emission so native crashes carry a recent
// memory snapshot. Context updates are local (no network); only every Nth tick also emits
// Application Metrics gauges.
const SCOPE_REFRESH_INTERVAL_MS = 10_000;
const TICKS_PER_METRIC_EMISSION = 6; // 6 × 10s = 60s between metric emissions

// Trail sampling is coarser than the scope refresh: TRAIL_STEP_S / (SCOPE_REFRESH_INTERVAL_MS
// in seconds). Derived rather than written twice so the two cannot drift apart.
const TICKS_PER_TRAIL_SAMPLE = TRAIL_STEP_S / (SCOPE_REFRESH_INTERVAL_MS / 1000);

const MEMORY_RUNTIME_CONTEXT = "memory_runtime";

const METRIC_CACHE_AIRCRAFT_ID_SIZE = "app.cache.aircraft_id.size";
const METRIC_MEMORY_JS_HEAP_USED = "app.memory.js_heap_used";
const METRIC_MEMORY_SYSTEM_LOW_MEMORY = "app.memory.system_low_memory";

// Each entry: which native field to read, which metric name to emit, and the context key for
// the scope-attached snapshot. Keeping the table here lets us add a metric without touching
// emission code.
const NATIVE_MEMORY_METRICS: ReadonlyArray<{
  field: keyof MemoryStatsResult;
  metric: string;
  context: string;
}> = [
  { field: "rss", metric: "app.memory.rss", context: "rss" },
  { field: "physFootprint", metric: "app.memory.phys_footprint", context: "phys_footprint" },
  { field: "totalPss", metric: "app.memory.total_pss", context: "total_pss" },
  { field: "nativePss", metric: "app.memory.native_pss", context: "native_pss" },
  { field: "javaHeapUsed", metric: "app.memory.java_heap_used", context: "java_heap_used" },
];

function readJsHeapUsed(): number | undefined {
  const heap = (performance as unknown as { memory?: { usedJSHeapSize?: number } }).memory;
  return typeof heap?.usedJSHeapSize === "number" ? heap.usedJSHeapSize : undefined;
}

async function safeCall<T>(label: string, fn: () => Promise<T>): Promise<T | undefined> {
  try {
    return await fn();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (/not implemented on web/i.test(msg)) {
      console.debug(`SentryCtx: ${label} unavailable on web`);
    } else {
      console.warn(`SentryCtx: ${label} failed`, err);
    }
    return undefined;
  }
}

// Sentry's trace_metric envelope is built JS-side and doesn't auto-tag release / os
// (sentry.release=null, os.name=null on every row verified empirically). Imported
// by every module that emits metrics; populated once during SentryContextService init.
export const metricBaseAttributes: Record<string, string> = {};

@Injectable({ providedIn: "root" })
export class SentryContextService {
  private settings = inject(SettingsService);
  private env = inject(EnvironmentService);

  private router = inject(Router);

  private deviceId: string | undefined;

  /** Rolling phys_footprint trajectory, merged into the memory_runtime context. */
  private readonly memoryTrail = new MemoryTrail();

  constructor() {
    void this.init();
  }

  private async init(): Promise<void> {
    // A Capgo live update replaces the web bundle under an unchanged native release,
    // so the release alone does not say which code ran. First, so early errors get it.
    const live = await safeCall("CapacitorUpdater.current", () => CapacitorUpdater.current());
    if (live) Sentry.setTag("capgoBundle", live.bundle.id === "builtin" ? "builtin" : live.bundle.version);

    this.deviceId = (await safeCall("Device.getId", () => Device.getId()))?.identifier;

    const info = await safeCall("Device.getInfo", () => Device.getInfo());
    if (info) metricBaseAttributes["os.name"] = info.platform;

    // Rebuild the release string in the format sentry-cocoa uses
    // (<bundleId>@<version>+<build>) so metric rows are filterable by app version.
    const appInfo = await safeCall("App.getInfo", () => App.getInfo());
    if (appInfo) metricBaseAttributes["sentry.release"] = `${appInfo.id}@${appInfo.version}+${appInfo.build}`;

    // settings$ is a BehaviorSubject — emits synchronously on subscribe, so
    // setUser fires once immediately with the resolved deviceId.
    this.settings.settings$
      .pipe(
        map((s) => s.registration),
        distinctUntilChanged()
      )
      .subscribe((registration) => {
        Sentry.setUser({ id: this.deviceId, username: registration });
        Sentry.setTag("registration", registration);
      });

    try {
      const api = await this.env.getApiInfo();
      Sentry.setTag("apiStage", api.stage);
    } catch (err) {
      console.warn("SentryCtx: env.getApiInfo failed", err);
    }

    this.startMetricSnapshot();
  }

  private startMetricSnapshot(): void {
    let tickCount = 0;
    let trailTick = 0;
    const snapshot = async (): Promise<void> => {
      try {
        const cacheSize = fppProvider.getAircraftIdCacheSize();
        const jsHeapUsed = readJsHeapUsed();

        let mem: MemoryStatsResult | undefined;
        try {
          mem = await MemoryStats.getCurrent();
        } catch {
          // plugin missing during web/serve build or transient native error — keep going
        }

        // Sample the trail BEFORE the metric-emission gate below, which returns early.
        // Reuses the reading already taken above, so this costs no extra native call
        // (getCurrent measured 0.32 ms median on an iPad14,2 — the trail adds none of it).
        if (trailTick++ % TICKS_PER_TRAIL_SAMPLE === 0) {
          this.memoryTrail.record(mem?.physFootprint, this.router.url);
        }

        // Refresh native scope every tick so a native crash captured by sentry-cocoa carries
        // the most recent (≤10s old) memory reading. @sentry/capacitor propagates setContext
        // to the native scope across the JS bridge.
        const context: Record<string, unknown> = {
          compressed: mem?.compressed,
          // Attached to the crash context only, deliberately not emitted as gauges: six more
          // 60 s series across the fleet is real metric volume, and the question these answer
          // ("how much headroom was left when it died") is a per-crash question, not a trend.
          footprint_peak: mem?.footprintPeak,
          limit_remaining: mem?.limitRemaining,
          available_memory: mem?.availableMemory,
          internal_bytes: mem?.internalBytes,
          external_bytes: mem?.externalBytes,
          compressed_peak: mem?.compressedPeak,
          dalvik_pss: mem?.dalvikPss,
          system_avail: mem?.systemAvail,
          system_low_memory: mem?.systemLowMemory,
          js_heap_used: jsHeapUsed,
          aircraft_id_cache_size: cacheSize,
          sampled_at: new Date().toISOString(),
          ...this.memoryTrail.toContext(),
        };
        for (const { field, context: key } of NATIVE_MEMORY_METRICS) {
          context[key] = mem?.[field];
        }
        Sentry.setContext(MEMORY_RUNTIME_CONTEXT, context);

        // Application Metrics emission: only every Nth tick to keep volume sane.
        if (tickCount++ % TICKS_PER_METRIC_EMISSION !== 0) return;

        Sentry.metrics.gauge(METRIC_CACHE_AIRCRAFT_ID_SIZE, cacheSize, { attributes: metricBaseAttributes });
        if (typeof jsHeapUsed === "number") {
          Sentry.metrics.gauge(METRIC_MEMORY_JS_HEAP_USED, jsHeapUsed, {
            unit: "byte",
            attributes: metricBaseAttributes,
          });
        }
        if (mem) {
          for (const { field, metric } of NATIVE_MEMORY_METRICS) {
            const value = mem[field];
            if (typeof value === "number") {
              Sentry.metrics.gauge(metric, value, { unit: "byte", attributes: metricBaseAttributes });
            }
          }
          if (mem.systemLowMemory === true) {
            Sentry.metrics.count(METRIC_MEMORY_SYSTEM_LOW_MEMORY, 1, { attributes: metricBaseAttributes });
          }
        }
      } catch {
        // measurement only — never block startup or runtime
      }
    };
    void snapshot();
    setInterval(() => void snapshot(), SCOPE_REFRESH_INTERVAL_MS);
  }
}
